import React, { useState, useEffect } from 'react';
import { PageLoading } from '../../components/ui/Skeleton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { adminService } from '../../services/adminService';
import MetricsOverview from './components/MetricsOverview';
import UserManagement from './components/UserManagement';
import ConferenceManagement from './components/ConferenceManagement';
import SystemActivity from './components/SystemActivity';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import BillingAdmin from './components/BillingAdmin';

const MasterAdminDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
      return;
    }

    // Check if user has admin role
    // The profile loads asynchronously. Without this, the guard runs on the
    // first render while userProfile is still null, reads that as "not an
    // admin", and redirects — so admins could never deep-link or refresh
    // an admin page, only reach it by client-side navigation after load.
    if (!userProfile) return;

    if (!(userProfile?.isPlatformAdmin ?? userProfile?.roles?.includes('admin'))) {
      navigate('/user-dashboard');
      return;
    }

    loadPlatformStats();
  }, [user, userProfile, navigate]);

  const loadPlatformStats = async () => {
    try {
      setLoading(true);
      const data = await adminService?.getPlatformStats();
      setStats(data);
    } catch (err) {
      setError(err?.message || t('masterAdminDashboard.errors.failedToLoadStats'));
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: t('masterAdminDashboard.tabs.overview'), icon: 'LayoutDashboard' },
    { id: 'users', label: t('masterAdminDashboard.tabs.userManagement'), icon: 'Users' },
    { id: 'conferences', label: t('masterAdminDashboard.tabs.conferenceManagement'), icon: 'FolderOpen' },
    { id: 'billing', label: t('masterAdminDashboard.tabs.billing', 'Billing'), icon: 'CreditCard' },
    { id: 'analytics', label: t('masterAdminDashboard.tabs.analytics'), icon: 'BarChart3' },
    { id: 'activity', label: t('masterAdminDashboard.tabs.systemActivity'), icon: 'Activity' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <PageLoading label={t('masterAdminDashboard.loading')} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />

      {/* Control-room header */}
      <div className="relative overflow-hidden bg-chrome text-chrome-foreground">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '44px 44px'
          }}
        />
        <div className="pointer-events-none absolute -right-24 -top-24 w-80 h-80 bg-primary/25 blur-[110px]" />
        <div className="relative page-shell py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 border border-white/15 bg-white/[0.04] px-2.5 py-1 text-[11px] font-medium text-white/70 mb-3">
                <span className="w-1.5 h-1.5 bg-success" />
                {t('masterAdminDashboard.overview.systemHealth.allOperational')}
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">{t('masterAdminDashboard.title')}</h1>
              <p className="text-sm text-white/55 mt-1">{t('masterAdminDashboard.subtitle')}</p>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2">
              <Icon name="ShieldCheck" size={18} className="text-chrome-accent" />
              <span className="text-xs font-semibold text-white">{t('masterAdminDashboard.adminAccess')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="page-shell py-8 -mt-6 relative">
        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/30 rounded-xl flex items-center gap-2">
            <Icon name="AlertCircle" size={16} className="text-error" />
            <p className="text-error font-medium text-sm">{error}</p>
          </div>
        )}

        {/* Metrics */}
        {stats && <MetricsOverview stats={stats} />}

        {/* Segmented tab bar */}
        <div className="mb-5 overflow-x-auto">
          <div className="inline-flex items-center gap-1 rounded-xl border border-border bg-card p-1 shadow-sm">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab?.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="bg-card border border-border rounded-lg shadow-sm p-5 md:p-6">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <h2 className="text-base font-bold text-foreground">{t('masterAdminDashboard.overview.title')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-border bg-muted/30 p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-3">{t('masterAdminDashboard.overview.quickActions.title')}</h3>
                  <div className="space-y-2">
                    <Button variant="outline" size="sm" onClick={() => setActiveTab('users')} className="w-full justify-start" iconName="Users" iconPosition="left">
                      {t('masterAdminDashboard.overview.quickActions.manageUsers')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setActiveTab('conferences')} className="w-full justify-start" iconName="FolderOpen" iconPosition="left">
                      {t('masterAdminDashboard.overview.quickActions.manageConferences')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setActiveTab('billing')} className="w-full justify-start" iconName="CreditCard" iconPosition="left">
                      {t('masterAdminDashboard.tabs.billing', 'Billing')}
                    </Button>
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-muted/30 p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-3">{t('masterAdminDashboard.overview.platformInfo.title')}</h3>
                  <dl className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <dt className="text-muted-foreground">Version</dt>
                      <dd className="font-mono text-foreground">1.0.0</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-muted-foreground">Environment</dt>
                      <dd className="font-mono text-foreground">Production</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-muted-foreground">Database</dt>
                      <dd className="inline-flex items-center gap-1.5 font-medium text-success">
                        <span className="w-1.5 h-1.5 bg-success" /> Active
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'conferences' && <ConferenceManagement />}
          {activeTab === 'billing' && <BillingAdmin />}
          {activeTab === 'analytics' && <AnalyticsDashboard />}
          {activeTab === 'activity' && <SystemActivity />}
        </div>
      </div>
    </div>
  );
};

export default MasterAdminDashboard;